import React, { useEffect, useMemo } from 'react';
import { useAppDispatch, useAppSelector } from 'hooks';
import { Container } from 'reactstrap';

import { faExclamationTriangle } from '~components/icon';
import { TableView } from '~components/table-view';

import getAwardAlertsColumns from '~weekly-planner/forms/award-alerts/list';

import { useGetAwardAlertsQuery } from '~weekly-planner/actions/awardAlerts';

import { clear, updateParams } from '~weekly-planner/reducers/awardAlerts';

import { selectDerivedWeekRange, selectShowSystemUsers } from '~weekly-planner/selectors';
import {
  selectAllAwards,
  selectAwardAlertsVisibilities,
  selectAwardCount,
  selectParams,
} from '~weekly-planner/selectors/appointments/awardAlerts';

import AwardAlertViewMenu from './AwardAlertViewMenu';

interface ComponentProps {
  isOpen: boolean;
}

const AwardAlerts: React.FC<ComponentProps> = React.memo(({ isOpen }) => {
  const dispatch = useAppDispatch();
  const params = useAppSelector(selectParams);
  const alerts = useAppSelector(selectAllAwards);
  const alertVisibilities = useAppSelector(selectAwardAlertsVisibilities);
  const count = useAppSelector(selectAwardCount);
  const showSystemUsers = useAppSelector(selectShowSystemUsers);
  const showSystemUsersParam = showSystemUsers ? { show_system_users: true } : {};

  const derivedWeekRange = useAppSelector(selectDerivedWeekRange);
  const { week_start } = derivedWeekRange;

  const { limit } = params;

  // compute include_alerts once when visibilities change
  const includeAlerts = useMemo(
    () => Object.keys(alertVisibilities).filter((key) => alertVisibilities[key]),
    [alertVisibilities],
  );

  // RTK Query Args
  const queryArgs = useMemo(
    () => ({
      ...params,
      ...(includeAlerts.length ? { include_alerts: includeAlerts } : {}),
      ...showSystemUsersParam,
    }),
    [params, includeAlerts, showSystemUsers],
  );

  // RTK Query Hook
  const { isLoading, isFetching } = useGetAwardAlertsQuery(queryArgs, {
    skip: !isOpen || !week_start || limit == null,
  });

  const onLoadMore = () => {
    const newLimit = (limit ?? 0) + 30;
    dispatch(updateParams({ ...params, limit: newLimit }));
  };

  useEffect(() => {
    if (isOpen) {
      dispatch(updateParams({ week_start, limit: 30, ...showSystemUsersParam }));
    }
  }, [isOpen, week_start, showSystemUsers]);

  // unmount & clear reducer
  useEffect(() => {
    return () => {
      dispatch(clear([]));
    };
  }, []);

  const awardAlertsColumns = getAwardAlertsColumns();

  // Memoize the mapped data to avoid re-render issues with randomUUID
  const tableData = useMemo(
    () =>
      alerts.map((alert) => ({
        ...alert,
        id: alert.id?.toString() ?? crypto.randomUUID(),
      })),
    [alerts],
  );

  return (
    <div className="award-alerts-list sidebar-list">
      <Container className="inner-content limited">
        <div className="nav">
          <AwardAlertViewMenu />
        </div>
        <div className="table-responsive">
          <TableView
            id="award-alerts"
            data={tableData}
            columns={awardAlertsColumns}
            readonly
            striped
            isLoading={isLoading || isFetching}
            allowPagination={true}
            onLoadMore={onLoadMore}
            totalCount={count}
            emptyIcon={faExclamationTriangle}
            emptyMessage="No alerts found"
          />
        </div>
      </Container>
    </div>
  );
});

AwardAlerts.displayName = 'AwardAlerts';
export default AwardAlerts;
